import { useCallback, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthProvider";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useIsMobile } from "../../../hooks/useMobile";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { localStorageService } from "../../../services/LocalStorageService";
import { MobileStaffingView } from "../../../utils/models";
import { isClassicStaffingPath } from "../../../utils/staffingRoutes";
import { isHeadNurse } from "../../../utils/utilFunctions";
import { resolveMobileStaffingView } from "./resolveMobileStaffingView";

type GapsViewPreference = {
	/** The view this user chose for this department, or `null` when they never chose one. */
	storedView: MobileStaffingView | null;
	/** Persist a choice for the current user + department and re-render everything reading it. */
	setStoredView: (view: MobileStaffingView) => void;
};

/**
 * Whether the gaps-first mobile Staffing view is OFFERED at all here — as a choice, flag or no flag.
 *
 * One predicate for the two places that fork on it — the route switch and the `AdminDrawer` row that
 * switches into it — because a drawer row pointing at a view the route will not render is a dead end
 * the user has no way to understand. Gates: phone viewport, head nurse, and the department types the
 * gaps model was built for (hospital and medical/synergy departments keep classic; both come from
 * `useHospitalData`, so this reads them rather than re-deriving them from the config). The rollout flag
 * is not one of them: it decides which screen opens by default (`resolveMobileStaffingView`), not
 * whether the gaps-first view can be opened.
 */
export const useIsGapsViewAvailable = (): boolean => {
	const { isMobile } = useIsMobile();
	const { userInfo } = useAuth();
	const { isHospital, isMedicalDepartment } = useHospitalData();
	return isMobile && isHeadNurse(userInfo.role) && !isHospital && !isMedicalDepartment;
};

/**
 * Whether the gaps-first view is what this user is actually looking at right now.
 *
 * The one place the rollout default lives. Two things fork on it and they must never disagree: the route
 * switch, which decides what Staffing renders, and `Layout`, which decides whether the bottom bar is on
 * screen. A bar over classic, or classic with no bar, are both broken screens.
 *
 * Which one: the user's saved choice for this department, or the rollout flag's when they never chose
 * (`resolveMobileStaffingView` — on opens the gaps-first view, off opens classic). The route switch reads
 * this same hook, so the rule lives in one place.
 *
 * The URL overrides the preference, never the other way round. `staffing/classic` and `staffing2` are
 * pinned to the classic screen in the route table itself, so on those two the preference is not what
 * the user is looking at — and the department switcher preserves the `classic` segment while the
 * preference is per-department, so landing on one of them with a gaps preference is an ordinary
 * two-tap journey, not a hand-typed URL. Without this the user got classic's list under the gaps bar,
 * with the hamburger hidden and no slot on the bar lit.
 */
export const useIsGapsViewActive = (): boolean => {
	const isAvailable = useIsGapsViewAvailable();
	const isRolloutFlagOn = useFeatureFlag(FEATURE_FLAGS.MobileGapsView);
	const { storedView } = useGapsViewPreference();
	const { pathname } = useLocation();
	if (isClassicStaffingPath(pathname)) return false;
	return isAvailable && resolveMobileStaffingView(storedView, isRolloutFlagOn) === MobileStaffingView.Gaps;
};

/**
 * Everything currently reading the preference, so a write from one component reaches all of them.
 *
 * The preference has two readers that are never in the same subtree — `MobileStaffingRouteSwitch`, which
 * decides what the Staffing route renders, and `AdminDrawer`, whose "back to gaps-first view" row writes
 * it. With per-hook `useState` the drawer's write updated only the drawer's own copy: the switch is
 * already mounted on `staffing/:dateString?`, so navigating back to that same route reconciles it as the
 * same element rather than remounting, and it kept rendering classic off a stale value until a manual
 * reload. An external store makes the write visible to every reader in the same commit.
 */
const preferenceSubscribers = new Set<() => void>();

const notifyPreferenceChanged = (): void => {
	for (const onChange of preferenceSubscribers) onChange();
};

/**
 * The remembered gaps-first-vs-classic choice for the current user and department.
 *
 * Read through `useSyncExternalStore` so every reader shares one value: `localStorage` is the source of
 * truth and a write notifies all of them. The snapshot is keyed on user + department, so the same mounted
 * hook follows a department change instead of showing the previous facility's choice.
 *
 * Returns `null` for "never chose" instead of defaulting here: which view a first visit lands on is a
 * rollout decision, and it belongs at the one place that knows the rollout (`resolveMobileStaffingView`).
 */
export const useGapsViewPreference = (): GapsViewPreference => {
	const { userInfo } = useAuth();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const userId = userInfo?.id;

	const subscribe = useCallback((onChange: () => void) => {
		preferenceSubscribers.add(onChange);
		return () => {
			preferenceSubscribers.delete(onChange);
		};
	}, []);

	// Returns a string enum or null — a primitive — so React can compare snapshots by value and this
	// never loops the way an object snapshot rebuilt each render would.
	const getSnapshot = useCallback(
		(): MobileStaffingView | null => (userId && departmentId ? localStorageService.getMobileStaffingView(departmentId, userId) : null),
		[departmentId, userId],
	);

	const storedView = useSyncExternalStore(subscribe, getSnapshot);

	const setStoredView = useCallback(
		(view: MobileStaffingView) => {
			if (!userId || !departmentId) return;
			localStorageService.setMobileStaffingView(departmentId, view, userId);
			notifyPreferenceChanged();
		},
		[departmentId, userId],
	);

	return { storedView, setStoredView };
};
